import { createSlice } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';

import {
  createCustomSubmission,
  createCustomSubmissionAnswer,
  removeCustomSubmissionAnswer,
  endCustomSubmissionQuestionTimer,
  finishCustomSubmission,
  getCustomSubmissionAiFeedback,
  getCustomSubmissionById,
  getCustomSubmissionQuestion,
  getSubmissionQuestionAIExplanation,
  startCustomSubmissionQuestionTimer,
  updateCustomSubmissionQuestionOpenAiFeedback,
} from './api';
import { RootState } from '../..';
import { IQuestion, IQuestionDetailAnswer } from '../../QuestionSlice';
import { IQuiz } from '../../QuizSlice';
import { ISubTopic } from '../../SubTopicSlice';
import { IUser } from '../../UserSlice';

export type TSubmissionStatus = 'pending' | 'completed';
export type TSubmissionQuestionStatus = 'pending' | 'completed' | 'skipped';

export interface ISubmissionQuestion {
  order: number;
  question: string;
  status: TSubmissionQuestionStatus;
  answers?: IQuestionDetailAnswer[];
  // Server-computed at finishSubmission: true iff selected set exactly matches correct set.
  isCorrect?: boolean;
  timerStart?: number;
  timerEnd?: number;
  openAiFeedback?: -1 | 0 | 1;
  openEnded?: {
    answer?: string;
    score?: number;
    comment?: string;
    gradedAt?: number;
    gradedBy?: string;
    openAiComment?: string;
  };
  isOpenEnded?: boolean;
}

export interface ICustomSubmission {
  _id: string;
  section: string;
  user: IUser;
  quiz: IQuiz;
  questions: ISubmissionQuestion[];
  score: number;
  shuffleFactor: number;
  status: TSubmissionStatus;
  totalScore: number;
  createdAt: string;
  completedAt: number;
  updatedAt?: string;
  isTimerEnabled?: boolean;
  timerStart?: number;
  timerEnd?: number;
  subTopic?: ISubTopic;
  aiFeedback?: {
    strength: string;
    improvement: string;
    recommendation: string;
  };
}

export interface ICustomSubmissionState {
  status: 'idle' | 'loading' | 'rejected';
  isListLoading: boolean;
  count: number;
  submission: ICustomSubmission | undefined;
  question: IQuestion | undefined;
  list: Array<ICustomSubmission>;
  quizDataForAboutPage?: { list: Array<{ paper: string; quiz: IQuiz }> };
  submitStatus: 'idle' | 'loading' | 'rejected';
  questionAIExplanation: string | undefined;
  aiExplanationStatus: 'idle' | 'loading' | 'rejected';
  aiFeedbackStatus: 'idle' | 'loading' | 'rejected';
}

const initialState: ICustomSubmissionState = {
  status: 'idle',
  isListLoading: false,
  count: 0,
  submission: undefined,
  question: undefined,
  list: [],
  quizDataForAboutPage: undefined,
  submitStatus: 'idle',
  questionAIExplanation: undefined,
  aiExplanationStatus: 'idle',
  aiFeedbackStatus: 'idle',
};

export const CustomSubmissionSlice = createSlice({
  name: 'customSubmission',
  initialState,
  reducers: {
    resetCustomSubmission: (state) => {
      state.submission = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // User Create Submission by Custom Quiz
      .addCase(createCustomSubmission.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createCustomSubmission.fulfilled, (state, action) => {
        state.status = 'idle';

        if (action.payload && action.payload.submission) {
          const { submission, customQuiz } = action.payload;

          const history = createBrowserHistory();

          history.push(
            `${process.env.PUBLIC_URL}/dashboard/${submission.section}/custom-quiz/${submission._id}`
          );

          window.location.reload();
        }
      })
      .addCase(createCustomSubmission.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER GET SUBMISSION BY ID
      .addCase(getCustomSubmissionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCustomSubmissionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(getCustomSubmissionById.rejected, (state) => {
        state.status = 'rejected';
      })

      // START SUBMISSION TIMER
      .addCase(
        startCustomSubmissionQuestionTimer.fulfilled,
        (state, action) => {
          if (
            state.submission &&
            action.payload &&
            action.payload.submissionId &&
            action.payload.questionId &&
            action.payload.timerStart
          ) {
            const newSubmission = { ...state.submission };

            if (newSubmission.questions && newSubmission.questions.length > 0) {
              const newSubmissionQuestions = newSubmission.questions;
              const index = newSubmission.questions.findIndex(
                (item) => item.question === action.payload.questionId
              );

              if (index > -1) {
                newSubmissionQuestions[index].timerStart =
                  action.payload.timerStart;
                newSubmission.questions = newSubmissionQuestions;
              }
            }

            state.submission = newSubmission;
          }
        }
      )
      // END QUESTION TIMER
      .addCase(endCustomSubmissionQuestionTimer.fulfilled, (state, action) => {
        if (
          state.submission &&
          action.payload &&
          action.payload.submissionId &&
          action.payload.questionId &&
          action.payload.timerEnd
        ) {
          const newSubmission = { ...state.submission };

          if (newSubmission.questions && newSubmission.questions.length > 0) {
            const newSubmissionQuestions = newSubmission.questions;
            const index = newSubmission.questions.findIndex(
              (item) => item.question === action.payload.questionId
            );

            if (index > -1) {
              newSubmissionQuestions[index].timerEnd = action.payload.timerEnd;
              newSubmission.questions = newSubmissionQuestions;
            }
          }

          state.submission = newSubmission;
        }
      })
      // SUBMISSION ANSWER CREATE
      .addCase(createCustomSubmissionAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createCustomSubmissionAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(createCustomSubmissionAnswer.rejected, (state) => {
        state.status = 'rejected';
      })
      // SUBMISSION ANSWER REMOVE (deselect)
      .addCase(removeCustomSubmissionAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeCustomSubmissionAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(removeCustomSubmissionAnswer.rejected, (state) => {
        state.status = 'rejected';
      })
      // FINISH SUBMISSION
      .addCase(finishCustomSubmission.pending, (state) => {
        state.submitStatus = 'loading';
      })
      .addCase(finishCustomSubmission.fulfilled, (state, action) => {
        state.submitStatus = 'idle';
        state.submission = action.payload;
      })
      .addCase(finishCustomSubmission.rejected, (state) => {
        state.submitStatus = 'rejected';
      })
      // USER GET SUBMISSION QUESTION
      .addCase(getCustomSubmissionQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCustomSubmissionQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(getCustomSubmissionQuestion.rejected, (state) => {
        state.status = 'rejected';
        state.question = undefined;
      })
      // USER GET SUBMISSION AI FEEDBACK
      .addCase(getCustomSubmissionAiFeedback.pending, (state) => {
        state.aiFeedbackStatus = 'loading';
        state.questionAIExplanation = undefined;
      })
      .addCase(getCustomSubmissionAiFeedback.fulfilled, (state, action) => {
        state.aiFeedbackStatus = 'idle';
        if (state.submission && action.payload.aiFeedback) {
          state.submission.aiFeedback = action.payload.aiFeedback;
        }
      })
      .addCase(getCustomSubmissionAiFeedback.rejected, (state) => {
        state.aiFeedbackStatus = 'rejected';
      })
      // USER GET SUBMISSION QUESTION
      .addCase(getSubmissionQuestionAIExplanation.pending, (state) => {
        state.aiExplanationStatus = 'loading';
        state.questionAIExplanation = undefined;
      })
      .addCase(
        getSubmissionQuestionAIExplanation.fulfilled,
        (state, action) => {
          state.aiExplanationStatus = 'idle';
          state.questionAIExplanation = action.payload;
        }
      )
      .addCase(getSubmissionQuestionAIExplanation.rejected, (state) => {
        state.aiExplanationStatus = 'rejected';
      })

      .addCase(
        updateCustomSubmissionQuestionOpenAiFeedback.pending,
        (state) => {
          state.status = 'loading';
        }
      )
      .addCase(
        updateCustomSubmissionQuestionOpenAiFeedback.fulfilled,
        (state, action) => {
          state.status = 'rejected';

          if (
            state.submission &&
            state.submission._id === action.payload.submissionId
          ) {
            const newSubmission: ICustomSubmission = {
              ...state.submission,
              questions: state.submission.questions.map((newQuestion) => {
                if (newQuestion.question === action.payload.questionId) {
                  newQuestion.openAiFeedback = action.payload.feedback;
                }

                return newQuestion;
              }),
            };

            state.submission = newSubmission;
          }
        }
      )
      .addCase(
        updateCustomSubmissionQuestionOpenAiFeedback.rejected,
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const { resetCustomSubmission } = CustomSubmissionSlice.actions;

export const getCustomSubmissionState = (
  state: RootState
): RootState['customSubmission'] => state.customSubmission;

export * from './api';
export default CustomSubmissionSlice.reducer;
